import {
  Accessor,
  Component,
  createContext,
  createResource,
  JSX,
  Resource,
  useContext,
} from 'solid-js';
import { loadTranslationsFromFile } from '@fulcrum/lib';
import { asError, ILogger } from '@trimble/logging';
import { useLogger } from './LoggerProvider';

/* eslint-disable @typescript-eslint/naming-convention */
const localeMap: Readonly<Record<string, string>> = {
  'en-us': 'en-US',
  'cs-cz': 'cs-CZ',
  'da-dk': 'da-DK',
  'de-de': 'de-DE',
  'es-es': 'es-ES',
  'fi-fi': 'fi-FI',
  'fr-fr': 'fr-FR',
  'it-it': 'it-IT',
  'ja-jp': 'ja-JP',
  'ko-kr': 'ko-KR',
  'nb-no': 'nb-NO',
  'nl-nl': 'nl-NL',
  'pt-br': 'pt-BR',
  'ru-ru': 'ru-RU',
  'sv-se': 'sv-SE',
  'zh-hant': 'zh-Hant',
  'zh-hans': 'zh-Hans',
  'zh-tw': 'zh-Hant',
  'zh-cn': 'zh-Hans',
  'hu-hu': 'hu-HU',
  'pl-pl': 'pl-PL',
};
/* eslint-enable @typescript-eslint/naming-convention */

const SUPPORTED_LOCALES = [
  'en-US',
  'de-DE',
  'fr-FR',
  'es-ES',
  'ja-JP',
  'ko-KR',
  'zh-Hans',
  'zh-Hant',
  'it-IT',
  'nl-NL',
  'da-DK',
  'nb-NO',
  'sv-SE',
  'fi-FI',
  'cs-CZ',
  'pl-PL',
  'hu-HU',
  'pt-BR',
  'ru-RU',
] as const;

async function initializeTranslations(
  userLanguage: string | undefined,
  localesPath: string,
  log: ILogger
): Promise<boolean> {
  try {
    const mappedLocale = userLanguage
      ? localeMap[userLanguage.toLowerCase()]
      : undefined;
    const locale = mappedLocale || navigator.language || 'en-US';
    const targetLocale =
      SUPPORTED_LOCALES.find((l) => l.startsWith(locale)) || 'en-US';

    await loadTranslationsFromFile(targetLocale, localesPath);
    log.info('Translations loaded for locale', { targetLocale, localesPath });
    return true;
  } catch (error: unknown) {
    log.error('Failed to load translations', undefined, asError(error));
    try {
      await loadTranslationsFromFile('en-US', localesPath);
      return true;
    } catch (fallbackError) {
      log.error(
        'Failed to load fallback translations',
        undefined,
        asError(fallbackError)
      );
      throw fallbackError;
    }
  }
}

interface TranslationContextValue {
  translations: Resource<boolean>;
  translationsReady: Accessor<boolean>;
  refetchTranslations(): void;
}

const TranslationContext = createContext<TranslationContextValue>();

export interface TranslationProviderProps {
  children?: JSX.Element;
  userLanguageReady: boolean;
  userLanguage: string | undefined;
  localesPath?: string;
}

export const TranslationProvider: Component<TranslationProviderProps> = (
  props
) => {
  const log = useLogger().getLogger('TranslationProvider');

  const [translations, { refetch: refetchTranslations }] = createResource(
    () => {
      if (props.userLanguageReady) {
        return {
          language: props.userLanguage,
          localesPath: props.localesPath ?? '/locales/',
        };
      }
      return undefined;
    },
    async (source) => {
      return await initializeTranslations(
        source.language,
        source.localesPath,
        log
      );
    }
  );

  const translationsReady: Accessor<boolean> = () =>
    translations() === true && !translations.error;

  return (
    <TranslationContext.Provider
      value={{
        translations,
        translationsReady,
        refetchTranslations: () => void refetchTranslations(),
      }}
    >
      {props.children}
    </TranslationContext.Provider>
  );
};

export function useTranslations(): TranslationContextValue {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error(
      'useTranslations must be used within a TranslationProvider'
    );
  }
  return context;
}
